import { useContext, useEffect, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import MDLink from "components/MDLink";
import {
  Button,
  Card,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  IconButton,
  InputAdornment,
} from "@mui/material";
import { Formik, Form } from "formik";
import * as Yup from "yup";
import ArrowBack from "@mui/icons-material/ArrowBack";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import { LoginFormContext } from "context/loginFormCtx";
import {
  addSuperAdminUser,
  getSuperAdminUserById,
  updateSuperAdminUser,
} from "lib/Api/SuperAdminUsers";
import { getRolePermissions } from "lib/Api/Roles";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";

const PASSWORD_REGEX = /^(?=.*[A-Za-z0-9])(?=.*[^A-Za-z0-9\s]).{8,}$/;

function AddEditSuperAdminForm({ superAdminData }) {
  const location = useLocation();
  const { id } = useParams();
  const navigate = useNavigate();
  const isEditMode = location.state?.isEditMode || false;
  const { setNotification } = useContext(LoginFormContext);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const handleClickShowPassword = () => {
    setShowPassword(!showPassword);
  };

  const handleClickShowConfirmPassword = () => {
    setShowConfirmPassword(!showConfirmPassword);
  };

  // Form validation schema
  const validationSchema = Yup.object().shape({
    first_name: Yup.string().required("First name is required"),
    last_name: Yup.string().required("Last name is required"),
    email: Yup.string().email("Invalid email").required("Email is required"),
    password: !isEditMode
      ? Yup.string()
          .trim()
          .required("Password is required")
          .matches(
            PASSWORD_REGEX,
            "Password must be minimum of 8 characters, alphanumeric and contain at least one special character."
          )
      : Yup.string()
          .optional()
          .nullable()
          .test("password", "Password is invalid", (value) => {
            if (value) {
              return PASSWORD_REGEX.test(value);
            }
            return true;
          }),
    confirm_password: !isEditMode
      ? Yup.string()
          .trim()
          .required("Confirm Password is required")
          .matches(PASSWORD_REGEX, "Password is invalid")
          .oneOf([Yup.ref("password"), ""], "Confirm password must be same as password")
      : Yup.string()
          .test("confirm-password-required", "Confirm Password is required", function (value) {
            const { password } = this.parent;
            if (password && password.trim() !== "") {
              return value !== undefined && value !== null && value.trim() !== "";
            }
            return true;
          })
          .test("passwords-match", "Confirm password must be same as password", function (value) {
            const { password } = this.parent;
            if (password && password.trim() !== "") {
              return value === password;
            }
            return true;
          })
          .test("password-valid", "Password is invalid", function (value) {
            const { password } = this.parent;
            if (password && password.trim() !== "" && value === password) {
              return PASSWORD_REGEX.test(value);
            }
            return true;
          }),
    // status: Yup.number().required("Status is required"),
  });

  // Initial form values
  const initialValues = {
    first_name: superAdminData?.first_name,
    last_name: superAdminData?.last_name,
    email: superAdminData?.email || "",
    password: "",
    confirm_password: "",
    // status: superAdminData?.status !== undefined ? superAdminData.status : 1,
  };

  const onSubmit = async (values, { setSubmitting, resetForm }) => {
    try {
      // Prepare data for API
      const apiData = {
        first_name: values.first_name,
        last_name: values.last_name,
        email: values.email,
        // status: values.status,
      };

      // Add password fields only if provided
      if (values.password) {
        apiData.password = values.password;
        // apiData.confirm_password = values.confirm_password;
      }

      if (isEditMode && id) {
        apiData.id = id;
        const response = await updateSuperAdminUser(apiData);
        if (response.status === 200) {
          setNotification({
            color: "success",
            icon: "check",
            title: "Success",
            content: "Super admin user updated successfully",
            open: true,
          });
          navigate("/super-admin");
        } else {
          setNotification({
            color: "error",
            icon: "warning",
            title: "Error",
            content: response.data?.message || "Failed to update super admin user",
            open: true,
          });
        }
      } else {
        const response = await addSuperAdminUser(apiData);
        if (response.status === 200) {
          setNotification({
            color: "success",
            icon: "check",
            title: "Success",
            content: "Super admin user added successfully",
            open: true,
          });
          navigate("/super-admin");
        } else {
          setNotification({
            color: "error",
            icon: "warning",
            title: "Error",
            content: response.data?.message || "Failed to add super admin user",
            open: true,
          });
        }
      }
    } catch (error) {
      console.error("Error submitting form:", error);
      setNotification({
        color: "error",
        icon: "warning",
        title: "Error",
        content: "An error occurred while processing your request",
        open: true,
      });
    }
    setSubmitting(false);
  };

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={validationSchema}
      onSubmit={onSubmit}
      enableReinitialize
    >
      {({
        values,
        errors,
        touched,
        handleChange,
        handleBlur,
        handleSubmit,
        isSubmitting,
        setFieldValue,
      }) => (
        <>
          <Form autoComplete="off">
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="First Name"
                  name="first_name"
                  value={values.first_name}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={touched.first_name && Boolean(errors.first_name)}
                  helperText={touched.first_name && errors.first_name}
                  autoComplete="off"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Last Name"
                  name="last_name"
                  value={values.last_name}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={touched.last_name && Boolean(errors.last_name)}
                  helperText={touched.last_name && errors.last_name}
                  autoComplete="off"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Email"
                  name="email"
                  type="email"
                  value={values.email}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={touched.email && Boolean(errors.email)}
                  helperText={touched.email && errors.email}
                  autoComplete="off"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Password"
                  name="password"
                  type={showPassword ? "text" : "password"}
                  value={values.password}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={touched.password && Boolean(errors.password)}
                  helperText={touched.password && errors.password}
                  autoComplete="new-password"
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          aria-label="toggle password visibility"
                          onClick={handleClickShowPassword}
                          edge="end"
                        >
                          {showPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Confirm Password"
                  name="confirm_password"
                  type={showConfirmPassword ? "text" : "password"}
                  value={values.confirm_password}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={touched.confirm_password && Boolean(errors.confirm_password)}
                  helperText={touched.confirm_password && errors.confirm_password}
                  autoComplete="new-password"
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          aria-label="toggle confirm password visibility"
                          onClick={handleClickShowConfirmPassword}
                          edge="end"
                        >
                          {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              {/* {isEditMode && (
                <Grid item xs={12} md={6}>
                  <FormControl fullWidth>
                    <InputLabel id="status-select-label">Status</InputLabel>
                    <Select
                      labelId="status-select-label"
                      id="status-select"
                      name="status"
                      value={values.status}
                      label="Status"
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={touched.status && Boolean(errors.status)}
                    >
                      <MenuItem value={1}>Active</MenuItem>
                      <MenuItem value={0}>Inactive</MenuItem>
                    </Select>
                  </FormControl>
                  {touched.status && errors.status && (
                    <p style={{ color: "#d32f2f", fontSize: "0.75rem", marginLeft: "14px" }}>
                      {errors.status}
                    </p>
                  )}
                </Grid>
              )} */}
            </Grid>
            <Grid item xs={12} sx={{ display: "flex", justifyContent: "flex-start" }}>
              <MDBox sx={{ my: 2 }} display="flex" justifyContent="center" alignItems="center">
                <Button disabled={isSubmitting} type="submit" sx={{ m: 1 }} variant="contained">
                  {isEditMode ? "Update User" : "Save User"}
                </Button>

                <MDLink to="/super-admin">
                  <Button variant="outlined">Cancel</Button>
                </MDLink>
              </MDBox>
            </Grid>
          </Form>
        </>
      )}
    </Formik>
  );
}

export default AddEditSuperAdminForm;
